import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Permission } from "apis/dataV2/permissions";
import { PipelineConfig } from "apis/dataV2/projectTypes";
import { CanvasContextMenuFeatureKey, defaultCanvasContextMenuFeatures } from "config/canvasContextMenu";
import {
    ButtonKey,
    defaultEnabledAdminWidgets,
    defaultEnabledWidgets,
    defaultLockedWidgets,
    featuresConfig,
    WidgetKey,
} from "config/features";
import { StorageKey } from "config/storage";
import { initScene } from "features/render";
import { SelectionResolution } from "features/render/types";
import { UnitSystem } from "features/uom/spec";
import { uniqueArray } from "utils/misc";
import { getFromStorage } from "utils/storage";

import { ProjectType, SceneType, State, UrlSearchQuery, UxFlavor } from "./types";
import {
    getCanvasContextMenuFeatures,
    getEnabledFeatures,
    getPrimaryMenu,
    getRequireConsent,
    getSceneType,
    getTakenWidgetSlotCount,
} from "./utils";

const favoriteWidgetsStorageKey = "favoriteWidgets";

const initialState: State = {
    isOnline: navigator.onLine,
    enabledWidgets: defaultEnabledWidgets,
    lockedWidgets: defaultLockedWidgets,
    sceneType: SceneType.Viewer,
    projectType: ProjectType.V1,
    projectV2Info: null!,
    pipelineConfig: undefined as PipelineConfig | undefined,
    tmZoneForCalc: undefined as string | undefined, // for project v1 - tmZone, for project v2 - proj4 def from epsg.io
    requireConsent: false,
    organization: "",
    projectName: "",
    isVersionComparison: false,
    currentProjectVersion: null,
    widgets: [],
    // TODO probably should be stored on the backend instead
    favoriteWidgets: JSON.parse(localStorage.getItem(favoriteWidgetsStorageKey) || "[]"),
    widgetSlot: {
        open: false,
        group: undefined,
    },
    widgetLayout: {
        widgets: 4,
        sideBySide: true,
        padWidgetsTop: false,
    },
    widgetGroupPanelState: {
        open: false,
        expanded: false,
    },
    maximized: [],
    maximizedHorizontal: [],
    minimized: undefined,
    primaryMenu: {
        button1: featuresConfig.home.key,
        button2: featuresConfig.cameraSpeed.key,
        button3: featuresConfig.flyToSelected.key,
        button4: featuresConfig.stepBack.key,
        button5: featuresConfig.stepForwards.key,
    },
    contextMenu: {
        canvas: {
            features: defaultCanvasContextMenuFeatures,
        },
    },
    urlSearchQuery: undefined,
    urlBookmarkId: undefined,
    localBookmarkId: undefined,
    config: {
        dataV2ServerUrl: import.meta.env.REACT_APP_DATA_V2_SERVER_URL ?? "https://data-v2.novorender.com",
        projectsUrl: import.meta.env.REACT_APP_PROJECTS_URL ?? "https://projects.novorender.com",
        authServerUrl: import.meta.env.REACT_APP_AUTH_SERVER_URL ?? "https://auth.novorender.com",
        bimCollabClientId: import.meta.env.REACT_APP_BIMCOLLAB_CLIENT_ID ?? "",
        bimTrackClientId: import.meta.env.REACT_APP_BIMTRACK_CLIENT_ID ?? "",
        jiraClientId: import.meta.env.REACT_APP_JIRA_CLIENT_ID ?? "",
        xsiteManageClientId: import.meta.env.REACT_APP_XSITEMANAGE_CLIENT_ID ?? "",
        novorenderClientId: import.meta.env.REACT_APP_NOVORENDER_CLIENT_ID ?? "",
        assetsUrl: import.meta.env.REACT_APP_ASSETS_URL ?? "https://assets.novorender.com",
        mixpanelToken: import.meta.env.REACT_APP_MIXPANEL_TOKEN ?? "",
        uxFlavor: import.meta.env.REACT_APP_UX_FLAVOR ?? "",
    },
    uxFlavor: UxFlavor.New,
    backendProvider: "novorender",
    procoreContext: null,
    snackbarMessage: null,
    highlightSetting: null,
    globalSearch: {
        open: false,
    },
    unitSystem: loadUnitSystem(),
    selectionResolution: loadSelectionResolution(),
    cameraIsTooFar: null,
    showUnusuallyFarObjects: false,
    sceneId: null,
    isProcoreBeta: false,
    showWeakDeviceMessage: false,
    newProjectVersionStatus: {
        available: false,
        dismissed: false,
    },
    joysticks: {
        moving: false,
    },
    initViewpointId: null,
    initViewpoint: null,
    appliedViewpoint: {
        viewpoint: null,
        appliedBy: "viewer-interaction",
    },
    isApplyingViewpoint: false,
    uiConfig: {
        overlayElementsZIndex: undefined,
    },
};

export const explorerSlice = createSlice({
    name: "explorer",
    initialState: initialState,
    reducers: {
        setEnabledWidgets: (state, action: PayloadAction<WidgetKey[]>) => {
            state.enabledWidgets = uniqueArray(action.payload);
        },
        enableWidgets: (state, action: PayloadAction<WidgetKey[]>) => {
            state.enabledWidgets = uniqueArray([...state.enabledWidgets, ...action.payload]);
        },
        lockWidgets: (state, action: PayloadAction<WidgetKey[]>) => {
            state.lockedWidgets = state.lockedWidgets.concat(action.payload);
        },
        unlockWidgets: (state, action: PayloadAction<WidgetKey[]>) => {
            state.lockedWidgets = state.lockedWidgets.filter((widget) => !action.payload.includes(widget));
        },
        setSceneType: (state, action: PayloadAction<SceneType>) => {
            state.sceneType = action.payload;
        },
        setWidgets: (state, action: PayloadAction<WidgetKey[]>) => {
            state.widgets = action.payload;
            state.maximized = state.maximized.filter((widget) => action.payload.includes(widget));
            state.maximizedHorizontal = state.maximizedHorizontal.filter((widget) => action.payload.includes(widget));
        },
        setMaximized: (state, action: PayloadAction<WidgetKey[]>) => {
            state.maximized = action.payload;
        },
        setMaximizedHorizontal: (state, action: PayloadAction<WidgetKey[]>) => {
            state.maximizedHorizontal = action.payload;
        },
        addWidgetSlot: (state, action: PayloadAction<WidgetKey>) => {
            state.widgets = state.widgets.concat(action.payload);
            maybeHideWidgetSlot(state);
        },
        replaceWidgetSlot: (state, action: PayloadAction<{ replace: WidgetKey; key: WidgetKey }>) => {
            state.minimized = undefined;

            if (state.maximized.includes(action.payload.replace)) {
                state.maximized = state.maximized.map((key) =>
                    key === action.payload.replace ? action.payload.key : key,
                );
            }

            if (state.maximizedHorizontal.includes(action.payload.replace)) {
                state.maximizedHorizontal = state.maximizedHorizontal.map((key) =>
                    key === action.payload.replace ? action.payload.key : key,
                );
            }

            const widgets = [...state.widgets];
            const indexToReplace = widgets.indexOf(action.payload.replace);

            if (indexToReplace !== -1) {
                widgets[indexToReplace] = action.payload.key;
            }

            state.widgets = widgets;
        },
        removeWidgetSlot: (state, action: PayloadAction<WidgetKey>) => {
            state.minimized = undefined;

            // better layout change for particular case
            const widgetIndex = state.widgets.indexOf(action.payload);
            if (
                widgetIndex === 0 &&
                state.widgets.length === 3 &&
                state.maximizedHorizontal.includes(state.widgets[1])
            ) {
                state.widgets[0] = state.widgets[2];
                state.widgets.splice(2, 1);
            }

            state.widgets = state.widgets.filter((slot) => slot !== action.payload);
            removeIrrelevantMaximized(state);
        },
        addFavoriteWidget: (state, action: PayloadAction<WidgetKey>) => {
            const key = action.payload;
            if (!state.favoriteWidgets.includes(key)) {
                state.favoriteWidgets.push(key);
            }
            // TODO consider storing on backend
            localStorage.setItem(favoriteWidgetsStorageKey, JSON.stringify(state.favoriteWidgets));
        },
        removeFavoriteWidget: (state, action: PayloadAction<WidgetKey>) => {
            const key = action.payload;
            state.favoriteWidgets = state.favoriteWidgets.filter((w) => w !== key);
            // TODO consider storing on backend
            localStorage.setItem(favoriteWidgetsStorageKey, JSON.stringify(state.favoriteWidgets));
        },
        setWidgetSlot: (state, action: PayloadAction<State["widgetSlot"]>) => {
            state.widgetSlot = action.payload;
        },
        setWidgetGroupPanelState: (state, action: PayloadAction<State["widgetGroupPanelState"]>) => {
            state.widgetGroupPanelState = action.payload;
        },
        forceOpenWidget: (state, action: PayloadAction<WidgetKey>) => {
            const open = state.widgets;
            const maximized = state.maximized;
            const maximizedHorizontal = state.maximizedHorizontal;
            const layout = state.widgetLayout;

            if (open.includes(action.payload)) {
                return;
            }

            if (getTakenWidgetSlotCount(open, maximized, maximizedHorizontal) < layout.widgets) {
                open.push(action.payload);
                return;
            }

            state.maximized = [];
            state.maximizedHorizontal = [];
            if (open.length >= layout.widgets) {
                open.pop();
            }
            open.push(action.payload);
        },
        setWidgetLayout: (state, action: PayloadAction<State["widgetLayout"]>) => {
            state.widgetLayout = action.payload;
        },
        setUrlBookmarkId: (state, action: PayloadAction<State["urlBookmarkId"]>) => {
            state.urlBookmarkId = action.payload;
        },
        setLocalBookmarkId: (state, action: PayloadAction<State["localBookmarkId"]>) => {
            state.localBookmarkId = action.payload;
        },
        setUrlSearchQuery: (state, action: PayloadAction<UrlSearchQuery | undefined>) => {
            state.urlSearchQuery = action.payload;

            if (!action.payload) {
                return;
            }

            const patterns = action.payload.pattern;
            if (
                action.payload.options.openWidgets &&
                ((Array.isArray(patterns) && patterns.length) || (!Array.isArray(patterns) && patterns))
            ) {
                state.widgets = [
                    featuresConfig.search.key,
                    action.payload?.options.selectionOnly === "3"
                        ? featuresConfig.selectionBasket.key
                        : featuresConfig.properties.key,
                ];
            }
        },
        toggleMaximized: (state, action: PayloadAction<WidgetKey>) => {
            if (state.maximized.includes(action.payload)) {
                state.maximized = state.maximized.filter((widget) => widget !== action.payload);

                return;
            }

            state.minimized = undefined;
            if (state.maximizedHorizontal.includes(action.payload)) {
                state.widgets = [action.payload];
                state.maximizedHorizontal = [action.payload];
                state.maximized = [action.payload];

                return;
            }

            if (state.maximizedHorizontal.length) {
                state.widgets = state.widgets.filter((w) => !state.maximizedHorizontal.includes(w));
                state.maximizedHorizontal = state.maximizedHorizontal.filter((w) => state.widgets.includes(w));
            }

            if (state.maximized.length) {
                state.widgets = state.widgets.filter(
                    (widget) => state.maximized.includes(widget) || widget === action.payload,
                );
            } else {
                const idx = state.widgets.indexOf(action.payload);
                if (state.uxFlavor === UxFlavor.New && !state.widgetLayout.sideBySide) {
                    // only 1 slot but can be maximized, do nothing
                } else if (state.uxFlavor === UxFlavor.New) {
                    // only non maximized widgets at this point
                    const canShift =
                        getTakenWidgetSlotCount(state.widgets, state.maximized, state.maximizedHorizontal) <
                        state.widgetLayout.widgets;

                    switch (idx) {
                        case 0:
                            if (!canShift) {
                                state.widgets.splice(1, 1);
                            }
                            break;
                        case 1:
                            if (canShift) {
                                [state.widgets[0], state.widgets[1]] = [state.widgets[1], state.widgets[0]];
                            } else {
                                state.widgets.splice(0, 1);
                            }
                            break;
                        case 2:
                            state.widgets.splice(3, 1);
                            break;
                        case 3:
                            state.widgets.splice(2, 1);
                            break;
                    }
                } else {
                    switch (idx) {
                        case 0:
                            state.widgets.splice(1, 1);
                            break;
                        case 1:
                            state.widgets.splice(0, 1);
                            break;
                        case 2:
                            state.widgets.splice(3, 1);
                            break;
                        case 3:
                            state.widgets.splice(2, 1);
                            break;
                    }
                }
            }

            state.maximized.push(action.payload);
            removeIrrelevantMaximized(state);
            maybeHideWidgetSlot(state);
        },
        toggleMaximizedHorizontal: (state, action: PayloadAction<WidgetKey>) => {
            if (state.maximizedHorizontal.includes(action.payload)) {
                state.maximizedHorizontal = state.maximizedHorizontal.filter((widget) => widget !== action.payload);

                return;
            }

            state.minimized = undefined;
            if (state.maximized.includes(action.payload)) {
                state.widgets = [action.payload];
                state.maximized = [action.payload];
                state.maximizedHorizontal = [action.payload];

                return;
            }

            if (state.maximized.length) {
                state.widgets = state.widgets.filter((w) => !state.maximized.includes(w));
                state.maximized = state.maximized.filter((w) => state.widgets.includes(w));
            }

            if (state.maximizedHorizontal.length) {
                const idx = state.widgets.indexOf(action.payload);
                if (idx === 2) {
                    state.widgets.splice(2, 1);
                    state.widgets.unshift(action.payload);
                }
                state.widgets = state.widgets.filter(
                    (widget) => state.maximizedHorizontal.includes(widget) || widget === action.payload,
                );
            } else {
                // only non maximized widgets at this point
                const idx = state.widgets.indexOf(action.payload);
                const canShift =
                    getTakenWidgetSlotCount(state.widgets, state.maximized, state.maximizedHorizontal) <
                    state.widgetLayout.widgets;

                switch (idx) {
                    case 0:
                        if (!canShift) {
                            state.widgets.splice(2, 1);
                        }
                        break;
                    case 1:
                        state.widgets.splice(3, 1);
                        break;
                    case 2:
                        if (canShift) {
                            state.widgets.splice(2, 1);
                            state.widgets.unshift(action.payload);
                        } else {
                            state.widgets[0] = action.payload;
                            state.widgets.splice(2, 1);
                        }
                        break;
                    case 3:
                        state.widgets.splice(3, 1);
                        state.widgets.splice(1, 1, action.payload);
                        break;
                }
            }

            state.maximizedHorizontal.push(action.payload);
            removeIrrelevantMaximized(state);
            maybeHideWidgetSlot(state);
        },
        setMinimized: (state, action: PayloadAction<State["minimized"]>) => {
            if (action.payload) {
                state.maximized = [];
                state.maximizedHorizontal = [];
            }
            state.minimized = action.payload;
        },
        clearMaximized: (state) => {
            state.maximized = [];
            state.maximizedHorizontal = [];
        },
        setRequireConsent: (state, action: PayloadAction<State["requireConsent"]>) => {
            state.requireConsent = action.payload;
        },
        setOrganization: (state, action: PayloadAction<State["organization"]>) => {
            state.organization = action.payload;
        },
        setPrimaryMenu: (state, action: PayloadAction<Partial<State["primaryMenu"]>>) => {
            state.primaryMenu = { ...state.primaryMenu, ...action.payload };
        },
        setCanvasContextMenu: (state, action: PayloadAction<Partial<State["contextMenu"]["canvas"]>>) => {
            state.contextMenu.canvas = { ...state.contextMenu.canvas, ...action.payload };
        },
        toggleIsOnline: (state, action: PayloadAction<State["isOnline"] | undefined>) => {
            state.isOnline = action.payload !== undefined ? action.payload : !state.isOnline;
        },
        setConfig: (state, action: PayloadAction<State["config"]>) => {
            state.config = { ...state.config, ...action.payload };
        },
        setCurrentProjectVersion: (state, action: PayloadAction<State["currentProjectVersion"]>) => {
            state.currentProjectVersion = action.payload;
        },
        setNewProjectVersionStatus: (state, action: PayloadAction<State["newProjectVersionStatus"]>) => {
            state.newProjectVersionStatus = { ...state.newProjectVersionStatus, ...action.payload };
        },
        setProjectPermissions: (state, action: PayloadAction<Permission[]>) => {
            if (state.projectV2Info) {
                state.projectV2Info.permissions = action.payload;
            }
        },
        setSnackbarMessage: (
            state,
            action: PayloadAction<{
                msg: string;
                closeAfter?: number;
                link?: string;
            } | null>,
        ) => {
            state.snackbarMessage = action.payload ? { ...action.payload, key: new Date().toISOString() } : null;
        },
        setUxFlavor: (state, action: PayloadAction<State["uxFlavor"]>) => {
            state.uxFlavor = action.payload;
        },
        setProcoreContext: (state, action: PayloadAction<State["procoreContext"]>) => {
            state.procoreContext = action.payload;
        },
        setBackendConfig: (
            state,
            action: PayloadAction<{
                provider: State["backendProvider"];
                authServerUrl?: string;
                dataV2ServerUrl?: string;
                projectsUrl?: string;
                assetsUrl?: string;
            }>,
        ) => {
            state.backendProvider = action.payload.provider;

            // Update URLs if provided, otherwise use defaults from backend config
            if (action.payload.authServerUrl) {
                state.config.authServerUrl = action.payload.authServerUrl;
            }
            if (action.payload.dataV2ServerUrl) {
                state.config.dataV2ServerUrl = action.payload.dataV2ServerUrl;
            }
            if (action.payload.projectsUrl) {
                state.config.projectsUrl = action.payload.projectsUrl;
            }
            if (action.payload.assetsUrl) {
                state.config.assetsUrl = action.payload.assetsUrl;
            }
        },
        setHighlightSetting: (state, action: PayloadAction<State["highlightSetting"]>) => {
            state.highlightSetting = action.payload;
        },
        setGlobalSearchOpen: (state, action: PayloadAction<boolean>) => {
            state.globalSearch.open = action.payload;
        },
        setUnitSystem: (state, action: PayloadAction<State["unitSystem"]>) => {
            state.unitSystem = action.payload;
        },
        setSelectionResolution: (state, action: PayloadAction<State["selectionResolution"]>) => {
            state.selectionResolution = action.payload;
        },
        setCameraIsTooFar: (state, action: PayloadAction<State["cameraIsTooFar"]>) => {
            state.cameraIsTooFar = action.payload;
        },
        setShowUnusuallyFarObjects: (state, action: PayloadAction<State["showUnusuallyFarObjects"]>) => {
            state.showUnusuallyFarObjects = action.payload;
        },
        setSceneId: (state, action: PayloadAction<State["sceneId"]>) => {
            state.sceneId = action.payload;
        },
        setIsProcoreBeta: (state, action: PayloadAction<State["isProcoreBeta"]>) => {
            state.isProcoreBeta = action.payload;
        },
        setShowWeakDeviceMessage: (state, action: PayloadAction<State["showWeakDeviceMessage"]>) => {
            state.showWeakDeviceMessage = action.payload;
        },
        setJoysticksMoving: (state, action: PayloadAction<State["joysticks"]["moving"]>) => {
            state.joysticks.moving = action.payload;
        },
        setInitViewpointId: (state, action: PayloadAction<State["initViewpointId"]>) => {
            state.initViewpointId = action.payload;
        },
        setInitViewpoint: (state, action: PayloadAction<State["initViewpoint"]>) => {
            state.initViewpoint = action.payload;
        },
        setAppliedViewpoint: (state, action: PayloadAction<State["appliedViewpoint"]>) => {
            state.appliedViewpoint = action.payload;
        },
        setAppliedViewpointToViewerInteraction: (state) => {
            if (state.isApplyingViewpoint || state.appliedViewpoint.appliedBy === "viewer-interaction") {
                return;
            }

            state.appliedViewpoint = {
                viewpoint: null,
                appliedBy: "viewer-interaction",
            };
        },
        setIsApplyingViewpoint: (state, action: PayloadAction<boolean>) => {
            state.isApplyingViewpoint = action.payload;
        },
        setUiConfig: (state, action: PayloadAction<State["uiConfig"]>) => {
            state.uiConfig = action.payload;
        },
    },
    extraReducers(builder) {
        builder.addCase(initScene, (state, action) => {
            const { customProperties } = action.payload.sceneData;

            state.projectType = action.payload.projectType;
            state.projectV2Info = action.payload.projectV2Info;
            state.pipelineConfig = action.payload.pipelineConfig;
            state.tmZoneForCalc = action.payload.tmZoneForCalc;
            state.sceneType = getSceneType(customProperties);
            state.requireConsent = getRequireConsent(customProperties);
            state.projectName = action.payload.sceneData.title;
            state.isVersionComparison = action.payload.isVersionComparison;
            state.uxFlavor = action.payload.uxFlavor;

            state.lockedWidgets = state.lockedWidgets.filter(
                (widget) =>
                    !customProperties?.features || !(customProperties?.features as Record<string, boolean>)[widget],
            );

            if (state.isVersionComparison) {
                Object.entries(featuresConfig).forEach(([key, config]) => {
                    if (
                        !("versionComparison" in config && config.versionComparison) &&
                        !state.lockedWidgets.includes(key as WidgetKey)
                    ) {
                        state.lockedWidgets.push(key as WidgetKey);
                    }
                });
            }

            if (action.payload.deviceProfile.isMobile && !state.lockedWidgets.includes(featuresConfig.images.key)) {
                state.lockedWidgets.push(featuresConfig.images.key);
            }
            state.enabledWidgets = uniqueArray(
                (
                    (customProperties.explorerProjectState?.features?.widgets?.enabled as WidgetKey[]) ??
                    getEnabledFeatures(customProperties)
                )
                    .concat(defaultEnabledAdminWidgets)
                    .concat(defaultEnabledWidgets),
            );

            if (customProperties.explorerProjectState?.features?.primaryMenu?.buttons) {
                const [button1, button2, button3, button4, button5] = customProperties.explorerProjectState.features
                    .primaryMenu.buttons as ButtonKey[];

                state.primaryMenu = {
                    button1,
                    button2,
                    button3,
                    button4,
                    button5,
                };
            } else {
                state.primaryMenu = getPrimaryMenu(customProperties) ?? state.primaryMenu;
            }

            if (customProperties.explorerProjectState?.features?.contextMenus?.canvas.primary.features) {
                const ctxMenuFeatures = customProperties.explorerProjectState.features.contextMenus.canvas.primary
                    .features as CanvasContextMenuFeatureKey[];
                state.contextMenu.canvas.features = ctxMenuFeatures;
            } else {
                state.contextMenu.canvas.features =
                    getCanvasContextMenuFeatures(customProperties) ?? state.contextMenu.canvas.features;
            }
        });
    },
});

function removeIrrelevantMaximized(state: State) {
    state.maximized = state.maximized.filter((w) => state.widgets.includes(w));
    state.maximizedHorizontal = state.maximizedHorizontal.filter((w) => state.widgets.includes(w));
}

function maybeHideWidgetSlot(state: State) {
    if (
        getTakenWidgetSlotCount(state.widgets, state.maximized, state.maximizedHorizontal) >=
            state.widgetLayout.widgets &&
        state.widgetSlot.open
    ) {
        state.widgetSlot.open = false;
    }
}

function loadUnitSystem() {
    const stored = getFromStorage(StorageKey.UnitSystem) as UnitSystem;
    if (stored && UnitSystem[stored]) {
        return stored;
    }

    const tzName = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Only select imperial if language is en-US and timezone is in America.
    // Ideally we should check if the user is in the US, but this is a good enough approximation.
    const isAmericaOrUs = tzName.startsWith("America/") || tzName.startsWith("US/");

    return navigator.language === "en-US" && isAmericaOrUs ? UnitSystem.imperial : UnitSystem.metric;
}

function loadSelectionResolution(): SelectionResolution {
    const stored = getFromStorage(StorageKey.SelectionResolution) as SelectionResolution;
    if (stored && Object.values(SelectionResolution).includes(stored)) {
        return stored;
    }
    return SelectionResolution.LastObject;
}

const { actions, reducer } = explorerSlice;
export { actions as explorerActions, reducer as explorerReducer };
